/**
 * Liquid Cooling CDU descriptor-engine save bridge (#997 Story 6.8) — the Liquid
 * Cooling instantiation of the generic `useRepeatableEngineSaveBridge` (mirror of
 * `useCoolingSystemEngineSaveBridge`).
 *
 * Replaces the parent-owned `useLiquidCoolingCduEditSession` for the persisted
 * (Request Detail) path once the Liquid Cooling step runs on
 * `DescriptorEngineSection`. Supplies the LIQUID COOLING bindings (query hook,
 * flush, canonical content-key comparator, invalidation keys, legacy synthesis,
 * editedData key) and renames the generic handle to the unchanged bespoke
 * `LiquidCoolingCduConfigurationsSaveHandle` contract so every existing call site
 * keeps working. The flush's created-id map is surfaced through
 * `flushPendingLiquidCoolingCduChanges` for the Obs 6 staged-upload remap.
 *
 * See `useRepeatableEngineSaveBridge` for the held-baseline / same-chain /
 * reset-to-flush-result semantics (the 6-3 review-patch cluster).
 */

import { useMemo } from 'react';

import {
  liquidCoolingCduConfigurationsKeys,
  useLiquidCoolingCduConfigurations,
} from './queries/useLiquidCoolingCduConfigurations';
import {
  useRepeatableEngineSaveBridge,
  type AggregateSaveBridgeExtras,
} from './useRepeatableEngineSaveBridge';
import { buildLegacyLiquidCoolingCduFromRequestFields } from '../components/liquidcoolingcdus/liquidCoolingCduListUtils';
import {
  flushLiquidCoolingCduEditSession,
  liquidCoolingCduEditSessionHasChanges,
} from '../services/liquidCoolingCduEditSessionFlush';

import type { RepeatableInstanceSaveHandle } from '../components/repeatableSections/repeatableInstanceSaveHandle';
import type { LiquidCoolingCduConfigurationsSaveHandle } from '../request/steps/liquidcooling/subsections/liquidCoolingCduConfigurationsSaveHandle';
import type { LiquidCoolingCduConfiguration } from '../types/liquidCoolingCdu';

export type UseLiquidCoolingCduEngineSaveBridgeParams = {
  requestId: string | undefined;
  isEditing: boolean;
  isOfflineRequest: boolean;
  /** Flat request fields (request + editedData merge) — legacy unit synthesis. */
  legacyRequestFields?: Record<string, unknown>;
  /** The request payload's PERSISTED `liquidCoolingCduConfigurations` embed. */
  persistedEmbedLiquidCoolingCdus?: unknown;
  /** Live session list — `editedData.liquidCoolingCduConfigurations` (engine bridge writes it). */
  editedLiquidCoolingCduConfigurations?: unknown;
  /** True when Liquid Cooling was touched this session (`dirtyFields.has('liquidCoolingCduConfigurations')`). */
  liquidCoolingCduConfigurationsDirty: boolean;
  applyEditedDataPatch?: (patch: Record<string, unknown>) => void;
  restoredBaseline?: LiquidCoolingCduConfiguration[] | null;
  onDirtyChange?: (dirty: boolean) => void;
};

export function useLiquidCoolingCduEngineSaveBridge({
  requestId,
  isEditing,
  isOfflineRequest,
  legacyRequestFields,
  persistedEmbedLiquidCoolingCdus,
  editedLiquidCoolingCduConfigurations,
  liquidCoolingCduConfigurationsDirty,
  applyEditedDataPatch,
  restoredBaseline,
  onDirtyChange,
}: UseLiquidCoolingCduEngineSaveBridgeParams): {
  saveHandle:
    | (LiquidCoolingCduConfigurationsSaveHandle & AggregateSaveBridgeExtras)
    | null;
  registerInstanceSaveHandle: (
    handle: RepeatableInstanceSaveHandle | null,
  ) => void;
  /** The held edit-session baseline (for draft-snapshot persistence). */
  sessionBaseline: LiquidCoolingCduConfiguration[] | null;
} {
  const {
    saveHandle: genericHandle,
    registerInstanceSaveHandle,
    sessionBaseline,
  } = useRepeatableEngineSaveBridge<LiquidCoolingCduConfiguration>({
    requestId,
    isEditing,
    isOfflineRequest,
    legacyRequestFields,
    persistedEmbedInstances: persistedEmbedLiquidCoolingCdus,
    editedInstances: editedLiquidCoolingCduConfigurations,
    instancesDirty: liquidCoolingCduConfigurationsDirty,
    applyEditedDataPatch,
    restoredBaseline,
    onDirtyChange,
    editedDataKey: 'liquidCoolingCduConfigurations',
    useInstancesQuery: useLiquidCoolingCduConfigurations,
    hasChanges: liquidCoolingCduEditSessionHasChanges,
    flush: flushLiquidCoolingCduEditSession,
    invalidateListKeys: id => [
      liquidCoolingCduConfigurationsKeys.list(id),
      liquidCoolingCduConfigurationsKeys.grouped(id),
    ],
    buildLegacyFromRequestFields: buildLegacyLiquidCoolingCduFromRequestFields,
  });

  const saveHandle = useMemo(():
    | (LiquidCoolingCduConfigurationsSaveHandle & AggregateSaveBridgeExtras)
    | null => {
    if (!genericHandle) {
      return null;
    }
    return {
      isLiquidCoolingCduEditorOpen: genericHandle.isEditorOpen,
      hasUnsavedLiquidCoolingCduEditor: genericHandle.hasUnsavedEditor,
      commitOpenLiquidCoolingCduEditor: genericHandle.commitOpenEditor,
      saveUnsavedLiquidCoolingCduIfNeeded: genericHandle.saveUnsavedIfNeeded,
      hasPendingLiquidCoolingCduChanges: genericHandle.hasPendingChanges,
      flushPendingLiquidCoolingCduChanges: genericHandle.flushPendingChanges,
      tryDismissLiquidCoolingCduEditor: genericHandle.tryDismissEditor,
      discardOpenLiquidCoolingCduEditor: genericHandle.discardOpenEditor,
      // #1501 aggregate save seams (uniform names across all six families)
      getPendingDiff: genericHandle.getPendingDiff,
      absorbExternalFlushResult: genericHandle.absorbExternalFlushResult,
      remapSessionIds: genericHandle.remapSessionIds,
      resetSessionAfterScopeRemoval:
        genericHandle.resetSessionAfterScopeRemoval,
    };
  }, [genericHandle]);

  return { saveHandle, registerInstanceSaveHandle, sessionBaseline };
}
